/**
 * =============================================================================
 * Componente: AffiliateLayout
 * Versão: 2.0.0 (Redesign Premium — Mobile First)
 *
 * Descrição:
 *   Shell premium do painel do afiliado com identidade visual exclusiva
 *   (escopo .affiliate-shell). Mobile-first:
 *     - Mobile (<768px): MobileHeader (sticky) + Outlet + BottomNav fixa
 *       com 5 itens. O item "Mais" abre um Sheet inferior (Saques,
 *       Materiais, Perfil, Sair).
 *     - Desktop (≥768px): DesktopSidebar fixa à esquerda + Outlet com
 *       header próprio interno por página.
 * =============================================================================
 */
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { AffiliateMobileHeader } from './shell/AffiliateMobileHeader';
import { AffiliateBottomNav } from './shell/AffiliateBottomNav';
import { AffiliateMoreSheet } from './shell/AffiliateMoreSheet';
import { AffiliateDesktopSidebar } from './shell/AffiliateDesktopSidebar';
import { AffiliateNotificationBell } from './AffiliateNotificationBell';
import { NewReferralModal } from './NewReferralModal';
import { Button } from '@/components/ui/button';
import { useAffiliatePortalRealtime } from '@/hooks/useAffiliatePortalRealtime';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { useAffiliateBranding, buildBrandingCssVars } from '@/hooks/useAffiliateBranding';

export function AffiliateLayout() {
  // Sheet "Mais" do bottom-nav (mobile)
  const [moreOpen, setMoreOpen] = useState(false);
  // Modal de "Nova Indicação" — disponível em todas as páginas via header
  const [newReferralOpen, setNewReferralOpen] = useState(false);

  // Realtime do portal: notificações, indicações e comissões
  useAffiliatePortalRealtime();

  // Branding personalizado do tenant — sobrescreve CSS vars do affiliate-shell.
  // O hook hidrata `data` de forma síncrona a partir do localStorage quando
  // já existe cache para o tenant, evitando o flash da paleta roxa padrão.
  const { affiliate } = useAffiliateAuth();
  const {
    data: brandingData,
    isLoading: isBrandingLoading,
    isError: isBrandingError,
  } = useAffiliateBranding(affiliate?.tenant_id ?? null);
  const brandingStyle = buildBrandingCssVars(brandingData?.branding);

  // Se ainda não temos branding (1º acesso absoluto, sem cache local) e
  // não houve erro, marcamos o shell como "pending" para aplicar uma paleta
  // neutra via CSS — assim não exibimos o roxo padrão como se fosse a marca.
  const brandingPending =
    !brandingData?.branding && isBrandingLoading && !isBrandingError;

  return (
    <div
      className={`affiliate-shell flex min-h-screen w-full${
        brandingPending ? ' aff-branding-pending' : ''
      }`}
      style={brandingStyle}
    >
      {/* Sidebar fixa em desktop */}
      <AffiliateDesktopSidebar />

      {/* Coluna principal */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header mobile */}
        <AffiliateMobileHeader onNewReferral={() => setNewReferralOpen(true)} />

        {/* Header desktop minimalista (botão "Nova Indicação" + sino à direita) */}
        <header className="hidden md:flex sticky top-0 z-20 h-14 items-center justify-end gap-2 bg-[hsl(var(--aff-bg))]/85 px-6 backdrop-blur-xl">
          <Button
            type="button"
            size="sm"
            onClick={() => setNewReferralOpen(true)}
            className="rounded-full bg-[hsl(var(--aff-primary))] text-[hsl(var(--aff-primary-foreground))] hover:bg-[hsl(var(--aff-primary)/0.9)] gap-1.5"
          >
            <UserPlus className="h-4 w-4" />
            Nova Indicação
          </Button>
          <AffiliateNotificationBell />
        </header>

        {/* Conteúdo: padding-bottom extra no mobile p/ não ficar sob a bottom-nav */}
        <main className="flex-1 overflow-x-hidden pb-24 md:pb-0">
          <Outlet />
        </main>
      </div>

      {/* Bottom-nav (mobile) + sheet "Mais" */}
      <AffiliateBottomNav onOpenMore={() => setMoreOpen(true)} />
      <AffiliateMoreSheet open={moreOpen} onOpenChange={setMoreOpen} />

      {/* Modal global de Nova Indicação (cadastro manual de lead) */}
      <NewReferralModal open={newReferralOpen} onOpenChange={setNewReferralOpen} />
    </div>
  );
}
